import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface DncEntry {
  id: string;
  scope: "account" | "contact";
  reason: string;
  source: string;
  notes: string | null;
  added_by: string;
  added_at: string;
  account_id: string | null;
  contact_id: string | null;
}

// Bulk-load all DNC rows (table is small — 11 rows live, capped at thousands).
// Cache 5 min — avoids per-account roundtrips when rendering large lists.
export function useDncIndex() {
  return useQuery({
    queryKey: ["dnc-index"],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("do_not_contact_list")
        .select("id, scope, reason, source, notes, added_by, added_at, account_id, contact_id");
      if (error) throw error;
      const rows = ((data || []) as unknown) as DncEntry[];
      const accountIds = new Set<string>();
      const contactIds = new Set<string>();
      const byAccount = new Map<string, DncEntry>();
      const byContact = new Map<string, DncEntry>();
      for (const r of rows) {
        if (r.scope === "account" && r.account_id) {
          accountIds.add(r.account_id);
          byAccount.set(r.account_id, r);
        }
        if (r.scope === "contact" && r.contact_id) {
          contactIds.add(r.contact_id);
          byContact.set(r.contact_id, r);
        }
      }
      return { rows, accountIds, contactIds, byAccount, byContact };
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useIsDnc(opts: { accountId?: string | null; contactId?: string | null }) {
  const { data } = useDncIndex();
  if (!data) return { blocked: false, entry: null as DncEntry | null };
  if (opts.contactId && data.contactIds.has(opts.contactId)) {
    return { blocked: true, entry: data.byContact.get(opts.contactId) || null };
  }
  if (opts.accountId && data.accountIds.has(opts.accountId)) {
    return { blocked: true, entry: data.byAccount.get(opts.accountId) || null };
  }
  return { blocked: false, entry: null };
}
